import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersAPI } from '../utils/api';
import { format } from 'date-fns';
import { ru,enUS, ka, et, hy, az, it, hi } from 'date-fns/locale';
import { FaArrowLeft } from 'react-icons/fa';
import '../styles/OrderDetails.css';
import { useTranslation } from 'react-i18next';

const OrderDetails = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getOrder(id);
      console.log(response.data);
      setOrder(response.data.data);
    } catch (error) {
      console.error('Ошибка загрузки заказа:', error);
      setError('Не удалось загрузить заказ');
    } finally {
      setLoading(false);
    }
  };

  const getDateLocale = () => {
    const locales = { ru, en: enUS, et, ka, hy, az, it, hi };
    return locales[i18n.language] || et;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t(`waitsForConfirmation`, 'Waits for confirmation'),
      confirmed: t(`confirmed`, 'Confirmed'),
      processing: t(`processing`, 'Processing'),
      completed: t(`completed`, 'Completed'),
      cancelled: t(`cancelled`, 'Cancelled'),
      rejected: t(`rejected`, 'Rejected')
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      rejected: 'status-rejected'
    };
    return classes[status] || '';
  };

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'kg': return t(`kg`, 'kg');
      case 'piece': return t(`piece`, 'tk');
      case 'box': return t(`box`, 'kast');
      default: return unit;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t(`loading`, 'Loading...')}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="error-container">
        <p>{error || 'Заказ не найден'}</p>
        <button onClick={() => navigate('/orders')} className="btn-primary">
          {t(`backToOrders`, 'Back to orders')}
        </button>
      </div>
    );
  }

  return (
    <div className="order-details-page">
      <button onClick={() => navigate('/orders')} className="back-btn">
        <FaArrowLeft />{t(`backToOrders`, 'Back to orders')}
      </button>

      <div className="order-details-container">
        <div className="order-details-header">
          <h1>{t(`order`, 'Order')} #{order.orderNumber}</h1>
          <div className={`order-status-large ${getStatusClass(order.status)}`}>
            {getStatusLabel(order.status)}
          </div>
        </div>

        <div className="order-info-grid">
          <div className="info-section">
            <h3>{t(`orderInfo`, 'Order info')}</h3>
            <div className="info-item">
              <strong>{t(`createdDate`, 'Created date')}:</strong>
              <span>{format(new Date(order.createdAt), 'd MMMM yyyy, HH:mm', { locale: getDateLocale() })}</span>
            </div>
            <div className="info-item">
              <strong>{t(`deliveryDate`, 'Delivery date')}:</strong>
              <span>
                {format(new Date(order.deliveryDate), 'd MMMM yyyy', { locale: getDateLocale() })}
              </span>
            </div>
            {order.notes && (
              <div className="info-item">
                <strong>Комментарий:</strong>
                <span>{order.notes}</span>
              </div>
            )}
          </div>

          <div className="info-section">
            <h3>{t(`deliveryAddress`, 'Delivery address')}</h3>
            <div className="info-item">
              <span>
                {order.customer?.address?.street}<br />
                {order.customer?.address?.postalCode} {order.customer?.address?.city}<br />
                {order.customer?.address?.country}
              </span>
            </div>
            <div className="info-item">
              <strong>{t(`contact`, 'Contact')}:</strong>
              <span>
                {order.customer?.contactPerson?.firstName} {order.customer?.contactPerson?.lastName}<br />
                {order.customer?.contactPerson?.phone}
              </span>
            </div>
          </div>
        </div>

        <div className="order-items-section">
          <h3>{t(`productsInOrder`, 'Products in order')}</h3>
          <div className="order-items-table">
            <div className="table-header">
              <div className="col-product">{t(`item`, 'Item')}</div>
              <div className="col-price">{t(`price`, 'Price')}</div>
              <div className="col-quantity">{t(`quantity`, 'Quantity')}</div>
              <div className="col-discount">{t(`discount`, 'Discount')}</div>
              <div className="col-total">{t(`total`, 'Total')}</div>
            </div>
            {order.items.map((item) => (
              <div key={item._id} className="table-row">
                <div className="col-product">
                  <div className="product-info">
                    <div>
                      <span>{item.name}</span>
                      {item.unit === 'box' && item.boxQuantity && (
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          ({item.boxQuantity} {t(`box`, 'kast')})
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-price">
                  {item.pricePerUnit.toFixed(2)} € / {getUnitLabel(item.unit)}
                </div>
                <div className="col-quantity">
                  {item.quantity} {getUnitLabel(item.unit)}
                  {item.unit === 'box' && item.boxQuantity && (
                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                      = {item.quantity * item.boxQuantity} {t(`piece`, 'tk')}
                    </div>
                  )}
                </div>
                <div className="col-discount">
                  {(() => {
                    if (item.discount === 0) return '-';
                    
                    if (item.discountType === 'custom_price') {
                      // Для кастомной цены вычисляем реальную скидку
                      const discountedPrice = item.totalPrice / item.quantity;
                      const discountAmount = item.pricePerUnit - discountedPrice;
                      return discountAmount > 0 ? `-${discountAmount.toFixed(2)} €` : '-';
                    }
                    
                    if (item.discountType === 'fixed') {
                      return `-${item.discount.toFixed(2)} € / ${getUnitLabel(item.product?.unit || 'piece')}`;
                    }
                    
                    return `-${item.discount}%`;
                  })()}
                </div>
                <div className="col-total">
                  {item.totalPrice.toFixed(2)} €
                </div>
              </div>
            ))}
          </div>

          <div className="order-summary">
            <div className="summary-row">
              <span>{t(`subTotal`, 'Subtotal')}:</span>
              <span>{order.subtotal.toFixed(2)} €</span>
            </div>
            {order.totalDiscount > 0 && (
              <div className="summary-row discount-row">
                <span>{t(`discount`, 'Discount')}:</span>
                <span>-{order.totalDiscount.toFixed(2)} €</span>
              </div>
            )}
            <div className="summary-row total-row">
              <strong>{t(`total`, 'Total')}</strong>
              <strong>{order.total.toFixed(2)} €</strong>
            </div>
          </div>
        </div>

        {order.statusHistory && order.statusHistory.length > 0 && (
          <div className="status-history-section">
            <h3>{t(`statusHistory`, 'Status history')}</h3>
            <div className="status-history">
              {order.statusHistory.slice().reverse().map((history, index) => (
                <div key={index} className="history-item">
                  <div className="history-date">
                    {format(new Date(history.changedAt), 'd MMMM yyyy, HH:mm', { locale: getDateLocale() })}
                  </div>
                  <div className="history-status">
                    <strong>{getStatusLabel(history.status)}</strong>
                  </div>
                  {history.comment && (
                    <div className="history-comment">
                      {(() => {
                        // Если комментарий - это автоматически сгенерированный ключ изменения статуса
                        if (history.comment.startsWith('statusChangedFrom_')) {
                          // Извлекаем старый и новый статусы из ключа
                          const parts = history.comment.replace('statusChangedFrom_', '').split('_to_');
                          if (parts.length === 2) {
                            const oldStatus = getStatusLabel(parts[0]);
                            const newStatus = getStatusLabel(parts[1]);
                            return `${oldStatus} → ${newStatus}`;
                          }
                        }
                        // Иначе пытаемся перевести как обычный ключ
                        return t(history.comment, history.comment);
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  
};



export default OrderDetails;

